import React, { useState } from 'react';
import { useAuth } from '../services/auth.service';
import { useAuthService } from '../services/auth-api.service';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
  Grid,
  LinearProgress,
  Fade,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const SettingsContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  maxWidth: '1200px',
  margin: '0 auto',
  minHeight: 'calc(100vh - 64px)',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
  paddingBottom: '16px',
  borderBottom: '2px solid #e9ecef',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#111b21',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      '& fieldset': {
        borderColor: '#667eea',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: '#666',
    '&.Mui-focused': {
      color: '#667eea',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 32px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '16px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
  },
  '&:disabled': {
    background: '#e9ecef',
    color: '#adb5bd',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 32px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '16px',
  border: '2px solid #667eea',
  color: '#667eea',
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f0f4ff',
    borderColor: '#5a6fd8',
    color: '#5a6fd8',
  },
}));

const TokenDisplayBox = styled(Paper)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '16px',
  wordBreak: 'break-all',
  fontFamily: 'monospace',
  fontSize: '14px',
  position: 'relative',
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#e7f3ff',
  border: '1px solid #b3d9ff',
  marginTop: '16px',
}));

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#0066cc',
  lineHeight: 1.6,
}));

export default function Settings() {
  const { user } = useAuth();
  const { changePassword, generatePermanentToken } = useAuthService();
  
  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Permanent Token State
  const [permanentToken, setPermanentToken] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handlePasswordChange = (field) => (e) => {
    setPasswordData({
      ...passwordData,
      [field]: e.target.value,
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword.trim()) {
      setSnackbar({
        open: true,
        message: 'Current password is required',
        severity: 'error',
      });
      return false;
    }
    if (!passwordData.newPassword.trim()) {
      setSnackbar({
        open: true,
        message: 'New password is required',
        severity: 'error',
      });
      return false;
    }
    if (passwordData.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: 'New password must be at least 6 characters long',
        severity: 'error',
      });
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error',
      });
      return false;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      setSnackbar({
        open: true,
        message: 'New password must be different from current password',
        severity: 'error',
      });
      return false;
    }
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      setSnackbar({
        open: true,
        message: 'Password changed successfully!',
        severity: 'success',
      });
      
      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to change password. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setTokenLoading(true);
    try {
      const response = await generatePermanentToken();
      setPermanentToken(response.permanentToken);
      setSnackbar({
        open: true,
        message: 'Permanent token generated successfully!',
        severity: 'success',
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to generate permanent token. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCopyToken = async () => {
    try {
      await navigator.clipboard.writeText(permanentToken);
      setTokenCopied(true);
      setSnackbar({
        open: true,
        message: 'Token copied to clipboard!',
        severity: 'success',
      });
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to copy token',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <SettingsContainer>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#111b21' }}>
          Settings
        </Typography>
        <Typography variant="body1" sx={{ color: '#6c757d' }}>
          Manage your account security and authentication settings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Change Password Section */}
        <Grid item xs={12} md={6}>
          <Fade in={true} timeout={600}>
            <SectionCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <LockIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <SectionTitle>Change Password</SectionTitle>
                </SectionHeader>

                <StyledTextField
                  fullWidth
                  label="Current Password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  disabled={passwordLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#6c757d' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('current')}
                          edge="end"
                          disabled={passwordLoading}
                        >
                          {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  label="New Password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  disabled={passwordLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#6c757d' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('new')}
                          edge="end"
                          disabled={passwordLoading}
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  disabled={passwordLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#6c757d' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirm')}
                          edge="end"
                          disabled={passwordLoading}
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {passwordLoading && (
                  <LinearProgress sx={{ mb: 2, borderRadius: '8px' }} />
                )}

                <StyledButton
                  fullWidth
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </StyledButton>
              </CardContent>
            </SectionCard>
          </Fade>
        </Grid>

        {/* Generate Permanent Token Section */}
        <Grid item xs={12} md={6}>
          <Fade in={true} timeout={800}>
            <SectionCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <KeyIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <SectionTitle>Permanent Token</SectionTitle>
                </SectionHeader>

                <InfoBox>
                  <InfoText>
                    <strong>Important:</strong> Permanent tokens do not expire and remain valid until the JWT secret is changed. 
                    Use them with caution and store them securely. They can be used for API authentication just like regular tokens.
                  </InfoText>
                </InfoBox>

                {permanentToken && (
                  <TokenDisplayBox>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          color: '#111b21',
                        }}
                      >
                        {permanentToken}
                      </Typography>
                    </Box>
                    <Tooltip title={tokenCopied ? 'Copied!' : 'Copy token'}>
                      <IconButton
                        onClick={handleCopyToken}
                        size="small"
                        sx={{
                          color: tokenCopied ? '#28a745' : '#667eea',
                          '&:hover': {
                            backgroundColor: '#f0f4ff',
                          },
                        }}
                      >
                        {tokenCopied ? <CheckCircleIcon /> : <CopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </TokenDisplayBox>
                )}

                {tokenLoading && (
                  <LinearProgress sx={{ mt: 2, mb: 2, borderRadius: '8px' }} />
                )}

                <StyledButton
                  fullWidth
                  variant="contained"
                  onClick={handleGenerateToken}
                  disabled={tokenLoading}
                  sx={{ mt: 2 }}
                >
                  {tokenLoading ? 'Generating Token...' : 'Generate Permanent Token'}
                </StyledButton>

                {permanentToken && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px' }}>
                    Make sure to copy this token now. You won't be able to see it again after leaving this page.
                  </Alert>
                )}
              </CardContent>
            </SectionCard>
          </Fade>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: '8px', minWidth: '300px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SettingsContainer>
  );
}
